import { HubFeature } from '@ats/api/cm';
import { LocDefs, MapLocKeys } from '@cm/core/localization';

const instructionHTML = `
<p>Follow the instructions below to format a CSV file that will allow you to import Fulfillment Voucher tokens in bulk.</p>
<p>Select the CSV file, then press the "Upload" button. Include the following column headers will be accepted in the CSV file.</p>
<ul>
	<li>Voucher ID</li>
	<li>Type</li>
</ul>
<p>Omitting the required columns may result in an error. Extra fields will be ignored.</p>
<p>Valid types are 25 and 50.</p>
<p>Note: There is a 500 row limit.</p>
`;

const dpRoot = 'Tools|CE Fee Vouchers';
type SPVoucherBulkUploadLocs =
	| 'SPVoucherBulkUploadTitle'
	| 'SPVoucherBulkUploadInstructions'
	| 'SPVoucherBulkShowInstructions'
	| 'SPVoucherBulkHideInstructions'
	| 'SPVoucherBulkUploadBatchIdLabel'
	| 'SPVoucherBulkUploadBatchIdRequired'
	| 'SPVoucherBulkUploadExpirationDateLabel'
	| 'SPVoucherBulkUploadExpirationDateInvalid'
	| 'SPVoucherBulkUploadFile'
	| 'SPVoucherBulkUploadFileHint'
	| 'SPVoucherBulkUploadFileRequired'
	| 'SPVoucherBulkUploadSubmitButton'
	| 'SPBulkVoucherUploadSuccess';

export const spVoucherBulkUploadLocDefs: LocDefs<SPVoucherBulkUploadLocs> = {
	SPVoucherBulkUploadTitle: {
		defaultValue: 'Upload Fee Vouchers Token File',
		description: 'Title for the upload fee vouchers token file section.',
		displayPath: `${dpRoot}|Title`,
		displayType: 'Title',
		feature: HubFeature.CredentialContinuingEducationCredentialContinuingEducationVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	SPVoucherBulkUploadInstructions: {
		defaultValue: instructionHTML,
		description: 'Instructions for the upload fee vouchers token file section.',
		displayPath: `${dpRoot}|Instructions`,
		displayType: 'Content',
		feature: HubFeature.CredentialContinuingEducationCredentialContinuingEducationVouchers,
		htmlAllowed: true,
		shared: false,
		portal: 'SP',
	},
	SPVoucherBulkShowInstructions: {
		defaultValue: 'Show Instructions',
		description: 'Button to show the upload fee vouchers token file section.',
		displayPath: `${dpRoot}|Show Instructions`,
		displayType: 'Button',
		feature: HubFeature.CredentialContinuingEducationCredentialContinuingEducationVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	SPVoucherBulkHideInstructions: {
		defaultValue: 'Hide Instructions',
		description: 'Button to hide the upload fee vouchers token file section.',
		displayPath: `${dpRoot}|Hide Instructions`,
		displayType: 'Button',
		feature: HubFeature.CredentialContinuingEducationCredentialContinuingEducationVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	SPVoucherBulkUploadBatchIdLabel: {
		defaultValue: 'Batch ID',
		description: 'Label for the batch ID input field.',
		displayPath: `${dpRoot}|Batch ID`,
		displayType: 'Label',
		feature: HubFeature.CredentialContinuingEducationCredentialContinuingEducationVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	SPVoucherBulkUploadBatchIdRequired: {
		defaultValue: 'Batch ID is required',
		description: 'Error message for the batch ID input field.',
		displayPath: `${dpRoot}|Batch ID Required`,
		displayType: 'Error',
		feature: HubFeature.CredentialContinuingEducationCredentialContinuingEducationVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	SPVoucherBulkUploadExpirationDateLabel: {
		defaultValue: 'Expiration Date',
		description: 'Label for the expiration date input field.',
		displayPath: `${dpRoot}|Expiration Date`,
		displayType: 'Label',
		feature: HubFeature.CredentialContinuingEducationCredentialContinuingEducationVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	SPVoucherBulkUploadExpirationDateInvalid: {
		defaultValue: 'Expiration Date is invalid',
		description: 'Error message for the expiration date input field.',
		displayPath: `${dpRoot}|Expiration Date Invalid`,
		displayType: 'Error',
		feature: HubFeature.CredentialContinuingEducationCredentialContinuingEducationVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	SPVoucherBulkUploadFile: {
		defaultValue: 'Input CSV Filename',
		description: 'Label for the file input field.',
		displayPath: `${dpRoot}|File`,
		displayType: 'Label',
		feature: HubFeature.CredentialContinuingEducationCredentialContinuingEducationVouchers,
		portal: 'SP',
		htmlAllowed: false,
		shared: false,
	},
	SPVoucherBulkUploadFileHint: {
		defaultValue: 'Files must not be larger than 20Mb',
		description: 'Hint for the file input field.',
		displayPath: `${dpRoot}|File Hint`,
		displayType: 'Content',
		feature: HubFeature.CredentialContinuingEducationCredentialContinuingEducationVouchers,
		portal: 'SP',
		htmlAllowed: false,
		shared: false,
	},
	SPVoucherBulkUploadFileRequired: {
		defaultValue: 'File is required',
		description: 'Error message for the file input field.',
		displayPath: `${dpRoot}|File Required`,
		displayType: 'Error',
		feature: HubFeature.CredentialContinuingEducationCredentialContinuingEducationVouchers,
		portal: 'SP',
		htmlAllowed: false,
		shared: false,
	},
	SPVoucherBulkUploadSubmitButton: {
		defaultValue: 'Upload',
		description: 'Label for the submit button.',
		displayPath: `${dpRoot}|Submit`,
		displayType: 'Button',
		feature: HubFeature.CredentialContinuingEducationCredentialContinuingEducationVouchers,
		portal: 'SP',
		htmlAllowed: false,
		shared: false,
	},
	SPBulkVoucherUploadSuccess: {
		defaultValue: 'Fee vouchers uploaded successfully',
		description: 'Success message for the upload fee vouchers token file section.',
		displayPath: `${dpRoot}|Upload Success`,
		displayType: 'Notification',
		feature: HubFeature.CredentialContinuingEducationCredentialContinuingEducationVouchers,
		portal: 'SP',
		htmlAllowed: false,
		shared: false,
	},
};

export const spVoucherBulkUploadLocs = MapLocKeys<SPVoucherBulkUploadLocs>(
	spVoucherBulkUploadLocDefs
);

export type SPVoucherBulkUploadLocKeys = typeof spVoucherBulkUploadLocs;
